import React, { useRef, useState } from 'react';
import { useMetricType } from 'hooks/useMetricType';
import { validateAddPercent, validateAddSize } from 'util/validatePsdInput';
import { IconButton, Tooltip } from '@mui/material';
import { useOutsideClickListener } from './useOutsideClickListener';
import { convertToDefaultMetric } from 'util/conversions';
import { isEmpty } from 'lodash';
import {
  PsdActionCell,
  PsdCheckboxCell,
  PsdGridRow,
  PsdPercentCell,
  PsdSizeCell,
} from 'containers/PropertyPanel/Cards/Feed/PsdGrid';
import { PsdNumberField } from 'containers/PropertyPanel/Cards/Feed/PsdNumberField';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { validateDecimalInput } from 'util/validateDecimal';
import { CancelButton } from 'containers/PropertyPanel/Cards/Feed/CancelButton';

interface PsdTableAddDataRowInterface {
  newPercentList: number[];
  newSizeList: number[];
  add(addSizeList: number[], addPercentList: number[], addIndex: number): void;
  hidePsd: boolean;
  close: () => void;
  conversionType: string | undefined;
}

export const PsdTableAddDataRow = ({
  newPercentList,
  newSizeList,
  add,
  hidePsd,
  close,
  conversionType,
}: PsdTableAddDataRowInterface) => {
  const metricType = useMetricType();
  const [addPercent, setAddPercent] = useState<number | undefined>(undefined);
  const [addSize, setAddSize] = useState<number | undefined>(undefined);
  const convAddSize = convertToDefaultMetric({
    value: addSize,
    metricType,
    conversionType: conversionType,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const input2Ref = useRef<HTMLInputElement>(null);
  const validatedAddSize = validateAddSize(convAddSize, newSizeList);
  const validatedAddPercent = validateAddPercent(
    newPercentList,
    newSizeList,
    addPercent,
    convAddSize,
  );

  const addSizeSuccess = isEmpty(validatedAddSize);
  const addPercentSuccess = isEmpty(validatedAddPercent);

  const addPercentOk =
    hidePsd || (typeof addPercent === 'number' && addPercentSuccess);

  const addSuccess: boolean =
    typeof addSize === 'number' && addSizeSuccess && addPercentOk;

  const addHandler = () => {
    const addPercentList = [...newPercentList];
    const addSizeList = [...newSizeList];
    if (
      (typeof addPercent === 'number' || hidePsd) &&
      typeof convAddSize === 'number'
    ) {
      let addIndex = addSizeList.findIndex((s) => convAddSize < s);
      if (addIndex === -1) {
        addIndex = addSizeList.length;
      }
      typeof addPercent === 'number' &&
        addPercentList.splice(addIndex, 0, addPercent);
      addSizeList.splice(addIndex, 0, convAddSize);
      setAddPercent(undefined);
      setAddSize(undefined);
      add(addSizeList, addPercentList, addIndex);

      inputRef.current?.focus();
    }
  };
  const addRowRef = useOutsideClickListener(close);

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLDivElement>,
    goNext: React.RefObject<HTMLInputElement | null> | null = null,
  ) => {
    if (e.key === 'Enter') {
      if (addSuccess) {
        addHandler();
      } else if (!hidePsd && goNext) {
        goNext.current?.focus();
      }
    }

    if (e.key === 'Escape') {
      close();
    }
  };

  return (
    <PsdGridRow ref={addRowRef}>
      <PsdCheckboxCell>
        <CancelButton onClose={close} />
      </PsdCheckboxCell>

      <PsdSizeCell>
        <PsdNumberField
          inputRef={inputRef}
          autoFocus={true}
          value={addSize ?? ''}
          placeholder='Value'
          error={validatedAddSize}
          onKeyDown={(e) => handleKeyPress(e, input2Ref)}
          onChange={(e) => setAddSize(validateDecimalInput(e, 3))}
        />
      </PsdSizeCell>

      {!hidePsd && (
        <PsdPercentCell>
          <PsdNumberField
            inputRef={input2Ref}
            value={addPercent ?? ''}
            placeholder='Value'
            error={validatedAddPercent}
            onKeyDown={(e) => handleKeyPress(e)}
            onChange={(e) => setAddPercent(validateDecimalInput(e))}
          />
        </PsdPercentCell>
      )}

      <PsdActionCell>
        <Tooltip title={'Add row'}>
          <IconButton size='small' disabled={!addSuccess} onClick={addHandler}>
            <CheckCircleIcon color={addSuccess ? 'primary' : 'disabled'} />
          </IconButton>
        </Tooltip>
      </PsdActionCell>
    </PsdGridRow>
  );
};
